import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, of, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';
import { MemberMessageService } from '@app/core/services/MemberMessageService';
import { MemberMessageNotificationService } from '@app/core/services/MemberMessageNotificationService';
import { AuthenticationService } from '@app/core/services/AuthenticationService';
import * as actions from './member-messages.reducer';

export function messageError(error: any, fallback: string): string {
  if (error?.status === 404) return 'Meddelandet är inte längre tillgängligt.';
  if (error?.status === 409) return 'Meddelandet har ändrats. Hämta det igen för att läsa den aktuella versionen.';
  if (error?.status === 403) return 'Du har inte behörighet att visa eller ändra meddelandet.';
  return fallback;
}

@Injectable()
export class MemberMessagesEffects {
  private readonly end$ = this.actions$.pipe(ofType(actions.enter, actions.leave));
  list$ = createEffect(() => this.actions$.pipe(ofType(actions.loadList), withLatestFrom(this.store.select(actions.selectMessages)),
    switchMap(([action, state]) => this.api.list(action.query, state.adminUserId).pipe(
      map(page => actions.listLoaded({ page })),
      catchError(error => of(actions.failed({ operation: 'list', message: messageError(error, 'Det gick inte att hämta meddelandena. Försök igen.') }))),
      takeUntil(this.end$), takeUntil(this.actions$.pipe(ofType(actions.loadDetail)))
    ))));
  detail$ = createEffect(() => this.actions$.pipe(ofType(actions.loadDetail), withLatestFrom(this.store.select(actions.selectMessages)),
    switchMap(([action, state]) => this.api.detail(action.id, state.adminUserId).pipe(
      map(message => actions.detailLoaded({ message })),
      catchError(error => of(actions.failed({ operation: 'detail', message: messageError(error, 'Det gick inte att hämta meddelandet. Försök igen.') }))),
      takeUntil(this.end$), takeUntil(this.actions$.pipe(ofType(actions.loadList)))
    ))));
  read$ = createEffect(() => this.actions$.pipe(ofType(actions.detailDisplayed), withLatestFrom(this.store.select(actions.selectMessages)),
    filter(([action, state]) => !state.readOnly && !state.adminUserId && !this.auth.isImpersonating() &&
      state.detail?.Id === action.id && state.detail.Version === action.version && !state.detail.IsRead),
    exhaustMap(([action]) => this.api.read(action.id, action.version).pipe(
      tap(() => this.notifications.readChanged()),
      map(result => actions.readConfirmed({ id: action.id, version: action.version, firstReadAtUtc: result.FirstReadAtUtc })),
      catchError(error => of(actions.failed({ operation: 'read', message: messageError(error, 'Läsmarkeringen kunde inte sparas. Försök igen.') }))),
      takeUntil(this.end$), takeUntil(this.actions$.pipe(ofType(actions.loadDetail, actions.loadList)))
    ))));
  preferences$ = createEffect(() => this.actions$.pipe(ofType(actions.loadPreferences, actions.savePreferences),
    withLatestFrom(this.store.select(actions.selectMessages)),
    filter(([, state]) => !state.readOnly && !state.adminUserId && !this.auth.isImpersonating()),
    exhaustMap(([action]) => (action.type === actions.savePreferences.type
      ? this.api.savePreferences((action as ReturnType<typeof actions.savePreferences>).enabled) : this.api.preferences()).pipe(
      map(result => actions.preferencesLoaded({ enabled: result.PushEnabled })),
      catchError(() => of(actions.failed({ operation: 'preferences', message: 'Inställningen kunde inte hämtas eller sparas. Försök igen.' }))),
      takeUntil(this.end$)
    ))));

  constructor(private actions$: Actions, private api: MemberMessageService, private store: Store,
    private notifications: MemberMessageNotificationService, private auth: AuthenticationService) {}
}
